import { FC, Fragment, ReactNode, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { JiraPreviewIssue } from 'models'
import { OnyxiaTooltip } from 'ui/components/tooltip'
import { PreviewTableWrapper } from './filterPreviewTable.styles'
import { escapeRegExp } from 'lib/utils'

const COLUMNS = [
  { key: 'issue_type', label: 'Type' },
  { key: 'key', label: 'Key' },
  { key: 'summary', label: 'Summary' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'assignee', label: 'Assignee' }
] as const

interface Props {
  issues: JiraPreviewIssue[]
  /* The pane's search term, marked up in the cells it matched. */
  search?: string
}

/* Splitting on the escaped term keeps the separators, so the odd parts are the
 * matches — case is preserved because the original text is what gets split. */
const highlightMatch = (text: string, search: string): ReactNode => {
  const term = search.trim()
  if (!term) return text

  const parts = text.split(new RegExp(`(${escapeRegExp(term)})`, 'gi'))

  return parts.map((part, index) =>
    index % 2 ? (
      <span className="preview-cell-match" key={index}>
        {part}
      </span>
    ) : (
      part
    )
  )
}

/* Jira serves an icon per issue type; its text is the fallback. */
const TypeCell: FC<{ issue: JiraPreviewIssue; search: string }> = ({ issue, search }) => {
  const [iconFailed, setIconFailed] = useState(false)

  if (!issue.icon_url || iconFailed) {
    return <Typography className="preview-cell-text">{highlightMatch(issue.issue_type || '', search)}</Typography>
  }

  return (
    <OnyxiaTooltip title={issue.issue_type || ''} placement="top">
      <img
        className="preview-type-icon"
        src={issue.icon_url}
        alt={issue.issue_type || ''}
        onError={() => setIconFailed(true)}
      />
    </OnyxiaTooltip>
  )
}

const edgeClasses = (index: number) =>
  [index === 0 ? 'is-first' : '', index === COLUMNS.length - 1 ? 'is-last' : ''].filter(Boolean).join(' ')

/* The preview sample: a header bar and one outlined row per issue, columns in
 * the design's proportions. Fields Jira omits render as empty cells. */
const FilterPreviewTable: FC<Props> = ({ issues, search = '' }) => (
  <PreviewTableWrapper>
    {COLUMNS.map((column, index) => (
      <Box className={`preview-header-cell ${edgeClasses(index)}`} key={column.key}>
        <Typography className="preview-cell-text">{column.label}</Typography>
      </Box>
    ))}

    {issues.map((issue, index) => (
      <Fragment key={issue.key || index}>
        {COLUMNS.map((column, columnIndex) => (
          <Box
            key={column.key}
            className={[
              'preview-cell',
              index % 2 ? 'is-even' : '',
              column.key === 'key' ? 'is-key' : '',
              edgeClasses(columnIndex)
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {column.key === 'issue_type' ? (
              <TypeCell issue={issue} search={search} />
            ) : (
              <Typography className="preview-cell-text">{highlightMatch(issue[column.key] || '', search)}</Typography>
            )}
          </Box>
        ))}
      </Fragment>
    ))}
  </PreviewTableWrapper>
)

export default FilterPreviewTable
